/* eslint-disable max-lines */

import { GLOBAL_VALUE_LABELS } from '@/shared/lib/adminValueFormat';
import { formatAgeRating } from '@/shared/lib/films';

import { IAdminResourceConfig } from '@/shared/types/admin-api';

import {
  ACTOR_COMPLAINT_CASE_STATUS_OPTIONS,
  ACTOR_COMPLAINT_CASES_API_PATH,
  ACTOR_GIRTHS,
  ADMIN_ROUTES,
  PARTICIPANT_COMPLAINT_CASES_API_PATH
} from '@/shared/constants';

/**
 * Единственный источник правды для статусов «Коллекции фильмов»: и фильтр
 * списка (`Page.tsx`), и тест берут значения/подписи отсюда — раньше их
 * дублировали ещё и `editFields` ресурса (руками статус больше не правится,
 * см. `ImportedFilmStatusActions` в `AdminImportedFilmDetail.tsx`).
 */
export const IMPORTED_FILM_STATUSES = (
  ['collected', 'offered', 'converted', 'declined'] as const
).map(value => ({ label: GLOBAL_VALUE_LABELS[value], value }));

/**
 * Пять типов фильма коллекции (`ImportedFilm.kind`, значения ПоискКино). Единый
 * источник для фильтра «Тип» и нейтрального тона плашки в `Page.tsx`: у
 * `ImportedFilm` нет enum в /admin/v1/classifiers, поэтому список статичный.
 */
export const IMPORTED_FILM_KINDS = [
  'movie',
  'tv-series',
  'cartoon',
  'anime',
  'animated-series'
] as const;

export const ADMIN_GENERIC_RESOURCE_CONFIG: Record<
  string,
  IAdminResourceConfig
> = {
  [ADMIN_ROUTES.users.index]: {
    apiPath: 'users',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'users',
    description:
      'Аккаунты актёров и режиссёров: роль, этап регистрации, контакты.',
    editFields: [
      'phone',
      'email',
      'password',
      'first_name',
      'last_name',
      'middle_name',
      {
        control: 'select',
        name: 'activity',
        options: { key: 'user_activities', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'state',
        options: { key: 'user_states', source: 'classifiers' }
      },
      'birth_date',
      {
        control: 'select',
        name: 'gender',
        options: { key: 'user_genders', source: 'classifiers' }
      },
      { accept: 'image/*', control: 'file', name: 'photo' },
      { accept: 'image/*', control: 'file', name: 'system_photo' },
      'occupation_id',
      'legal_entity_id',
      'address',
      'additional_info',
      {
        control: 'select',
        name: 'hair_color',
        options: { key: 'user_hair_color', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'hair_length',
        options: { key: 'user_hair_length', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'eye_color',
        options: { key: 'user_eye_color', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'body_type',
        options: { key: 'user_body_type', source: 'classifiers' }
      },
      'height',
      'weight',
      ...ACTOR_GIRTHS.map(({ key }) => key),
      'top_size',
      'bottom_size',
      'shoe_size',
      {
        control: 'select',
        name: 'marital_status',
        options: { key: 'user_marital_status', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'legal_status',
        options: { key: 'user_legal_status', source: 'classifiers' }
      },
      'salary',
      {
        control: 'select',
        name: 'salary_period',
        options: { key: 'user_salary_period', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'communication_method',
        options: { key: 'user_communication_method', source: 'classifiers' }
      },
      'foreign_filming',
      'allowed_to_use_profile',
      'performed_in_ad',
      'performed_in_movie',
      'performed_in_show',
      'performed_in_video_blog',
      {
        control: 'select',
        name: 'profile_visibility',
        options: { key: 'user_profile_visibility', source: 'classifiers' }
      },
      'profile_fullness_percent',
      'deleted_at',
      'instagram',
      'facebook',
      'vk',
      'youtube',
      'tik_tok',
      'odnoklassniki',
      'other_site',
      'kinopoisk',
      'kino_teatr',
      'rutube',
      'kinolift',
      'vk_video',
      'language_ids',
      'interest_ids',
      'specialization_ids',
      'interesting_occupation_ids',
      'features',
      'speech_features',
      'filming_restrictions',
      'restricted_users',
      'driver_license',
      'sport_ids',
      'dance_style_ids',
      'musical_instrument_ids',
      'vocal_style_ids',
      'extra_ids',
      'work_permissions',
      'city_ids',
      'citizenship_ids',
      'appearance_types'
    ],
    listColumns: [
      'id',
      'photo',
      'system_photo',
      'first_name',
      'last_name',
      'phone',
      'email',
      'activity',
      'profile_fullness_percent',
      'profile_visibility',
      'created_at',
      'updated_at'
    ],
    mode: 'list',
    recordKey: 'user',
    title: 'Все пользователи'
  },
  [ADMIN_ROUTES.users.show()]: {
    apiPath: 'users/:id',
    description: 'Карточка пользователя с анкетой и связанными данными.',
    mode: 'detail',
    recordKey: 'user',
    title: 'Профиль пользователя'
  },
  [ADMIN_ROUTES.users.admins]: {
    apiPath: 'admin_users',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'admin_users',
    description: 'Администраторы админки и их роли.',
    editFields: [
      'first_name',
      'last_name',
      'email',
      'phone',
      {
        control: 'select',
        name: 'role',
        options: { key: 'admin_user_roles', source: 'classifiers' }
      },
      'password',
      'password_confirmation'
    ],
    listColumns: [
      'id',
      'full_name',
      'phone',
      'email',
      'role',
      'status',
      'last_sign_in_at',
      'current_sign_in_at',
      'sign_in_count',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'admin_user',
    title: 'Администраторы'
  },
  [ADMIN_ROUTES.comments]: {
    apiPath: 'comments',
    canBatch: true,
    canExport: true,
    collectionKey: 'comments',
    description: 'Внутренние заметки администраторов на карточках записей.',
    listColumns: [
      'id',
      'resource_type',
      'resource_id',
      'resource_label',
      'author_label',
      'namespace',
      'body',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'comment',
    title: 'Комментарии'
  },
  [ADMIN_ROUTES.moderation.images]: {
    apiPath: 'moderation/images',
    collectionKey: 'images',
    description: 'Фотографии пользовательских портфолио на модерации.',
    editFields: [
      {
        control: 'select',
        name: 'moderation_status',
        options: {
          options: [
            { label: 'На модерации', value: 'pending' },
            { label: 'Одобрено', value: 'approved' },
            { label: 'Отклонено', value: 'blocked' }
          ],
          source: 'static'
        }
      }
    ],
    listColumns: ['id', 'item', 'user_id', 'moderation_status', 'created_at'],
    mode: 'list',
    recordKey: 'image',
    title: 'Модерация фото'
  },
  [ADMIN_ROUTES.moderation.videos]: {
    apiPath: 'moderation/videos',
    collectionKey: 'videos',
    description: 'Видео пользовательских портфолио и статусы обработки.',
    editFields: ['moderation_status', 'encoding_status'],
    listColumns: [
      'id',
      'user_id',
      'title',
      'video_type',
      'encoding_status',
      'moderation_status',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'video',
    title: 'Модерация видео'
  },
  [ADMIN_ROUTES.companies]: {
    apiPath: 'companies',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'companies',
    description: 'Компании и продакшены, связанные с проектами.',
    editFields: [
      'name',
      'legal_name',
      // ОГРН (13 цифр) или ОГРНИП (15 цифр) — ключ предложения фильма по удостоверению УНФ (19744)
      'ogrn',
      {
        control: 'select',
        name: 'category',
        options: { key: 'company_categories', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'founder_id',
        // `recordKey`: строка «Основатель» в карточке — ФИО из `founder`, а не сырой id (19745)
        options: {
          apiPath: 'users/lookup',
          recordKey: 'founder',
          source: 'lookup'
        }
      },
      { accept: 'image/*', control: 'file', name: 'logo' },
      'contact_email',
      'contact_phone'
    ],
    listColumns: [
      'id',
      'logo',
      'name',
      'legal_name',
      'category',
      'founder',
      'contact_phone',
      'contact_email'
    ],
    mode: 'list',
    recordKey: 'company',
    sections: [
      {
        fields: ['name', 'category', 'founder_id'],
        id: 'main',
        title: 'Основное'
      },
      {
        fields: ['legal_name', 'ogrn'],
        id: 'legal',
        title: 'Юридические данные'
      },
      {
        fields: ['contact_email', 'contact_phone'],
        id: 'contacts',
        title: 'Контакты'
      },
      {
        fields: ['logo'],
        id: 'logo',
        title: 'Логотип'
      }
    ],
    title: 'Компании'
  },
  [ADMIN_ROUTES.actor_cards]: {
    apiPath: 'actor_cards',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'actor_cards',
    description: 'Реестр актёров, созданный кинокомпаниями.',
    editFields: [
      {
        control: 'select',
        name: 'author_id',
        options: { apiPath: 'users/lookup', source: 'lookup' }
      },
      {
        control: 'select',
        name: 'actor_id',
        options: { apiPath: 'users/lookup', source: 'lookup' }
      },
      'last_name',
      'first_name',
      'middle_name',
      { control: 'date', name: 'birth_date', required: true },
      {
        control: 'select',
        name: 'gender',
        options: { key: 'actor_card_genders', source: 'classifiers' }
      },
      {
        control: 'select',
        name: 'status',
        options: { key: 'actor_card_statuses', source: 'classifiers' }
      },
      { accept: 'image/*', control: 'file', name: 'photo' }
    ],
    listColumns: [
      'id',
      'photo',
      'full_name',
      'birth_date',
      'gender',
      'status',
      'author',
      'actor',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'actor_card',
    sections: [
      {
        fields: [
          'author_id',
          'actor_id',
          'last_name',
          'first_name',
          'middle_name',
          'birth_date',
          'gender',
          'status'
        ],
        id: 'main',
        title: 'Основное'
      },
      {
        fields: ['photo'],
        id: 'photo',
        title: 'Фото'
      }
    ],
    title: 'Реестр актёров'
  },
  [ADMIN_ROUTES.legal_entities]: {
    apiPath: 'legal_entities',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'legal_entities',
    description: 'Записи об электронной подписи юридических лиц.',
    editFields: ['signature', 'signed_hash'],
    listColumns: ['id', 'signed_hash', 'user'],
    mode: 'list',
    recordKey: 'legal_entity',
    title: 'Юридические лица'
  },
  [ADMIN_ROUTES.projects.index]: {
    apiPath: 'projects',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'projects',
    description: 'Проекты, публикация и жизненный цикл.',
    editFields: [
      'name',
      'description',
      'project_link',
      { accept: 'image/*', control: 'file', name: 'cover' },
      'filming_start_date',
      'filming_end_date',
      { control: 'number', name: 'year' },
      'original_title',
      {
        control: 'select',
        formatValue: formatAgeRating,
        name: 'age_rating',
        options: {
          options: [
            { label: '0+', value: '0' },
            { label: '6+', value: '6' },
            { label: '12+', value: '12' },
            { label: '16+', value: '16' },
            { label: '18+', value: '18' }
          ],
          source: 'static'
        }
      },
      { control: 'number', name: 'movie_length' },
      { control: 'date', name: 'premiere_russia' },
      { control: 'date', name: 'premiere_world' },
      'on_main_page',
      'publish',
      {
        control: 'select',
        name: 'author_id',
        options: {
          apiPath: 'users/lookup',
          recordKey: 'author',
          source: 'lookup'
        }
      },
      {
        control: 'select',
        name: 'project_category_id',
        options: {
          apiPath: 'project_categories/lookup',
          recordKey: 'project_category',
          source: 'lookup'
        }
      },
      'delete_cover',
      {
        control: 'multiselect',
        name: 'scriptwriter_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'scriptwriter' },
          recordKey: 'scriptwriters',
          source: 'lookup'
        }
      },
      {
        control: 'multiselect',
        name: 'director_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'director' },
          recordKey: 'directors',
          source: 'lookup'
        }
      },
      {
        control: 'multiselect',
        name: 'producer_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'producer' },
          recordKey: 'producers',
          source: 'lookup'
        }
      },
      {
        control: 'multiselect',
        name: 'operator_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'operator' },
          recordKey: 'operators',
          source: 'lookup'
        }
      },
      {
        control: 'multiselect',
        name: 'composer_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'composer' },
          recordKey: 'composers',
          source: 'lookup'
        }
      },
      {
        control: 'multiselect',
        name: 'production_designer_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'production_designer' },
          recordKey: 'production_designers',
          source: 'lookup'
        }
      },
      {
        control: 'multiselect',
        name: 'editor_ids',
        options: {
          apiPath: 'filmmakers/lookup',
          params: { type: 'editor' },
          recordKey: 'editors',
          source: 'lookup'
        }
      }
    ],
    listColumns: [
      'id',
      'name',
      'status',
      'author',
      'project_category',
      'castings_count',
      'filming_period'
    ],
    mode: 'list',
    recordKey: 'project',
    sections: [
      {
        fields: [
          'name',
          'description',
          'project_link',
          'author_id',
          'project_category_id'
        ],
        id: 'main',
        title: 'Основное'
      },
      {
        fields: [
          'filming_start_date',
          'filming_end_date',
          'on_main_page',
          'publish'
        ],
        id: 'schedule',
        title: 'Сроки и публикация'
      },
      {
        fields: ['cover', 'delete_cover'],
        id: 'cover',
        title: 'Обложка'
      },
      {
        fields: [
          'year',
          'original_title',
          'age_rating',
          'movie_length',
          'premiere_russia',
          'premiere_world'
        ],
        id: 'film',
        title: 'О фильме'
      },
      {
        fields: [
          'director_ids',
          'scriptwriter_ids',
          'producer_ids',
          'operator_ids',
          'composer_ids',
          'production_designer_ids',
          'editor_ids'
        ],
        id: 'crew',
        title: 'Съёмочная группа'
      }
    ],
    title: 'Проекты'
  },
  [ADMIN_ROUTES.projects.show()]: {
    apiPath: 'projects/:id',
    description: 'Карточка проекта с параметрами публикации.',
    mode: 'detail',
    recordKey: 'project',
    title: 'Проект'
  },
  [ADMIN_ROUTES.castings.index]: {
    apiPath: 'castings',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'castings',
    description: 'Кастинги, статусы публикации и сроки заявок.',
    editFields: ['name', 'status', 'application_deadline_date'],
    mode: 'list',
    recordKey: 'casting',
    title: 'Кастинги'
  },
  [ADMIN_ROUTES.castings.show()]: {
    apiPath: 'castings/:id',
    description: 'Карточка кастинга с требованиями и статусом.',
    mode: 'detail',
    recordKey: 'casting',
    title: 'Кастинг'
  },
  [ADMIN_ROUTES.responses]: {
    apiPath: 'responses',
    canBatch: true,
    canExport: true,
    collectionKey: 'responses',
    description: 'Отклики актёров на кастинги.',
    listColumns: [
      'id',
      'casting',
      'project',
      'user',
      'status',
      'created_at',
      'updated_at'
    ],
    mode: 'list',
    readOnly: true,
    recordKey: 'response',
    title: 'Отклики'
  },
  [ADMIN_ROUTES.project_participants]: {
    apiPath: 'project_participants',
    canExport: true,
    collectionKey: 'project_participants',
    description: 'Участники, вручную добавленные в проекты из реестра актёров.',
    listColumns: [
      'id',
      'project',
      'project_status',
      'actor_card',
      'casting_role',
      'character_role',
      'created_at',
      'updated_at'
    ],
    mode: 'list',
    readOnly: true,
    recordKey: 'project_participant',
    title: 'Участники проектов'
  },
  [ADMIN_ROUTES.invitations]: {
    apiPath: 'invitations',
    canExport: true,
    collectionKey: 'invitations',
    description: 'Приглашения пользователей в кастинги.',
    listColumns: ['id', 'user', 'casting', 'project', 'status', 'created_at'],
    mode: 'list',
    readOnly: true,
    recordKey: 'invitation',
    title: 'Приглашения'
  },
  [ADMIN_ROUTES.moderation.actor_complaints]: {
    apiPath: ACTOR_COMPLAINT_CASES_API_PATH,
    canExport: true,
    collectionKey: 'actor_complaint_cases',
    description:
      'Жалобы режиссёров на анкеты актёров: одно дело на актёра, жалобы копятся в нём.',
    // Правится только статус; решение не меняет анкету и никого не уведомляет
    editFields: [
      {
        control: 'select',
        name: 'status',
        options: {
          options: ACTOR_COMPLAINT_CASE_STATUS_OPTIONS,
          source: 'static'
        },
        // без пустого варианта «Не выбрано»: статус дела всегда задан
        required: true
      }
    ],
    listColumns: [
      'id',
      'actor',
      'status',
      'complaints_count',
      'reporters_count',
      'reasons_summary',
      'last_complaint_at'
    ],
    mode: 'list',
    recordKey: 'actor_complaint_case',
    title: 'Жалобы на анкеты'
  },
  // Решения — только панелью в карточке дела: «Скрыть участника» требует причину,
  // поэтому страницы правки статуса (editFields) нет
  [ADMIN_ROUTES.moderation.participant_complaints]: {
    apiPath: PARTICIPANT_COMPLAINT_CASES_API_PATH,
    canExport: true,
    collectionKey: 'participant_complaint_cases',
    description:
      'Жалобы на участников в составе проектов: одно дело на участника, жалобы копятся в нём.',
    listColumns: [
      'id',
      'participant',
      'project',
      'status',
      'complaints_count',
      'self_reports_count',
      'reasons_summary',
      'last_complaint_at'
    ],
    mode: 'list',
    recordKey: 'participant_complaint_case',
    title: 'Жалобы на участников'
  },
  [ADMIN_ROUTES.feedbacks]: {
    apiPath: 'feedbacks',
    canExport: true,
    collectionKey: 'feedbacks',
    description:
      'Сообщения пользователей о проблемах: скриншот, URL, окружение.',
    // Единственное редактируемое поле — статус обработки; остальное приходит от пользователя
    editFields: [
      {
        control: 'select',
        name: 'status',
        options: {
          options: [
            { label: 'Новый', value: 'new' },
            { label: 'В работе', value: 'in_progress' },
            { label: 'Решён', value: 'resolved' }
          ],
          source: 'static'
        }
      }
    ],
    listColumns: [
      'id',
      'user',
      'user_activity',
      'source',
      'url',
      'text',
      'status',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'feedback',
    title: 'Обратная связь'
  },
  [ADMIN_ROUTES.films]: {
    apiPath: 'films',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'films',
    description: 'Фильмы из МКРФ и внутренние связи портфолио.',
    editFields: [
      'name',
      'release_year',
      {
        control: 'select',
        name: 'project_type',
        options: { key: 'film_project_types', source: 'classifiers' }
      },
      'is_short_film',
      'popularity_rank',
      'external_id'
    ],
    listColumns: [
      'id',
      'name',
      'release_year',
      'project_type',
      'is_short_film',
      'popularity_rank'
    ],
    mode: 'list',
    recordKey: 'film',
    title: 'Фильмы (МКРФ)'
  },
  [ADMIN_ROUTES.imported_films]: {
    apiPath: 'imported_films',
    canBatch: false,
    canCreate: false,
    canExport: true,
    collectionKey: 'imported_films',
    description:
      'Релизы с составом: предложение кинокомпаниям и перевод в проекты с ' +
      'визитками актёров.',
    // Записи создаёт только импорт датасета, статус меняется только кнопками
    // карточки («Отклонить» / «Вернуть в коллекцию») — без editFields нет ни
    // роута /edit, ни карандаша в списке (см. getEditHref в Page.tsx).
    // В таблицу идут первые семь (getAdminListColumns); студии (19234) — рядом
    // с названием и годом, «Ожидают ответа» ушло за срез: статус «Предложен»
    // уже говорит, что ответа ждут, а число видно в карточке
    listColumns: [
      'id',
      'poster_preview_url',
      'name',
      'year',
      'studios',
      'kind',
      'status',
      'pending_offers_count',
      'persons_count',
      'movie_length',
      'film',
      'project',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'imported_film',
    title: 'Фильмы в проекты'
  },
  [ADMIN_ROUTES.documents.templates]: {
    apiPath: 'pattern_files',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'pattern_files',
    description: 'Шаблоны файлов для документов и генерации.',
    editFields: ['name', { control: 'file', name: 'item' }],
    listColumns: ['id', 'name', 'item', 'created_at', 'updated_at'],
    mode: 'list',
    recordKey: 'pattern_file',
    title: 'Шаблоны документов'
  },
  [ADMIN_ROUTES.legal_docs]: {
    apiPath: 'documents',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'documents',
    description: 'Правовые документы и прикреплённые файлы.',
    editFields: [
      {
        control: 'select',
        name: 'kind',
        options: { field: 'kind', model: 'Document', source: 'enums' }
      },
      { control: 'file', name: 'file' }
    ],
    listColumns: ['id', 'kind', 'file', 'created_at', 'updated_at'],
    mode: 'list',
    recordKey: 'document',
    title: 'Правовые документы'
  },
  [ADMIN_ROUTES.excerpts]: {
    apiPath: 'excerpts',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'excerpts',
    description: 'Видео читка для кастингов.',
    editFields: [
      'title',
      'text',
      'requirements',
      {
        control: 'select',
        name: 'casting_id',
        options: { apiPath: 'castings/lookup', source: 'lookup' }
      }
    ],
    listColumns: ['id', 'title', 'casting', 'created_at', 'updated_at'],
    mode: 'list',
    recordKey: 'excerpt',
    title: 'Видео читка'
  },
  [ADMIN_ROUTES.landing.news]: {
    apiPath: 'landing/news',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'news',
    description: 'Новости лендинга и публикации.',
    editFields: [
      'title',
      'description',
      { accept: 'image/*', control: 'file', name: 'main_image' },
      'main_image_description',
      { control: 'richtext', name: 'text_block_first' },
      { control: 'richtext', name: 'quote_block_first' },
      { control: 'richtext', name: 'text_block_second' },
      { control: 'richtext', name: 'quote_block_second' },
      { control: 'richtext', name: 'text_block_third' },
      'video_html_embed',
      'external_video_link',
      { accept: 'video/*', control: 'file', name: 'video_file' },
      'external_video_description',
      {
        control: 'select',
        name: 'tag_id',
        options: { apiPath: 'landing/tags/lookup', source: 'lookup' }
      }
    ],
    listColumns: [
      'id',
      'title',
      'description',
      'main_image_description',
      'text_block_first',
      'created_at'
    ],
    mode: 'list',
    recordKey: 'news',
    title: 'Новости лендинга'
  },
  [ADMIN_ROUTES.landing.news_images]: {
    apiPath: 'landing/news_images',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'news_images',
    description: 'Изображения галереи новостей лендинга.',
    editFields: [
      {
        control: 'select',
        name: 'news_id',
        options: { apiPath: 'landing/news/lookup', source: 'lookup' }
      },
      'description',
      'author',
      'source',
      { accept: 'image/*', control: 'file', name: 'image' }
    ],
    listColumns: ['id', 'image', 'news', 'description', 'author', 'created_at'],
    mode: 'list',
    recordKey: 'news_image',
    title: 'Изображения новостей'
  },
  [ADMIN_ROUTES.landing.news_tags]: {
    apiPath: 'landing/tags',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'tags',
    description: 'Теги новостей лендинга.',
    editFields: ['name'],
    mode: 'list',
    recordKey: 'tag',
    title: 'Теги новостей'
  },
  [ADMIN_ROUTES.landing.tabs]: {
    apiPath: 'landing/tabs',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'tabs',
    description: 'Вкладки FAQ лендинга и порядок их вывода.',
    editFields: ['name', 'position'],
    mode: 'list',
    recordKey: 'tab',
    title: 'Вкладки лендинга'
  },
  [ADMIN_ROUTES.landing.faqs]: {
    apiPath: 'landing/faqs',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'faqs',
    description: 'Вопросы и ответы FAQ лендинга (по вкладкам).',
    editFields: [
      'question',
      { control: 'richtext', name: 'answer' },
      {
        control: 'select',
        name: 'tab_id',
        options: { apiPath: 'landing/tabs/lookup', source: 'lookup' }
      },
      'position'
    ],
    listColumns: ['id', 'question', 'answer', 'tab', 'position'],
    mode: 'list',
    recordKey: 'faq',
    title: 'FAQ лендинга'
  },
  [ADMIN_ROUTES.landing.castings]: {
    apiPath: 'landing/castings',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'landing_castings',
    description: 'Кастинги лендинга: карточки на публичной странице.',
    editFields: [
      'name',
      'character_description',
      'project_name',
      'role_type',
      'project_type',
      'application_deadline_date',
      'salary',
      {
        control: 'select',
        name: 'salary_period',
        options: {
          field: 'salary_period',
          model: 'Landing::Casting',
          source: 'enums'
        }
      },
      {
        control: 'multiselect',
        name: 'filming_location_ids',
        options: { apiPath: 'cities/lookup', source: 'lookup' }
      },
      'position'
    ],
    listColumns: [
      'id',
      'name',
      'project_name',
      'role_type',
      'project_type',
      'salary_period'
    ],
    mode: 'list',
    recordKey: 'landing_casting',
    title: 'Кастинги лендинга'
  },
  [ADMIN_ROUTES.landing.logos]: {
    apiPath: 'landing/logos',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'logos',
    description: 'Логотипы партнёров лендинга.',
    editFields: [
      { accept: 'image/*', control: 'file', name: 'image' },
      'position'
    ],
    mode: 'list',
    recordKey: 'logo',
    title: 'Логотипы'
  },
  [ADMIN_ROUTES.dictionaries.specializations]: {
    apiPath: 'specializations',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'specializations',
    description: 'Специализации пользователей и проектов.',
    editFields: ['name'],
    mode: 'list',
    recordKey: 'specialization',
    title: 'Специализации'
  },
  [ADMIN_ROUTES.dictionaries.role_types]: {
    apiPath: 'casting_roles',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'casting_roles',
    description: 'Типы ролей для кастингов.',
    editFields: ['name'],
    mode: 'list',
    recordKey: 'casting_role',
    title: 'Типы ролей'
  },
  [ADMIN_ROUTES.dictionaries.occupations]: {
    apiPath: 'occupations',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'occupations',
    description: 'Профессии и род занятий пользователей. Фича в разработке.',
    editFields: ['name', 'require_casting_role'],
    mode: 'list',
    recordKey: 'occupation',
    title: 'Профессии'
  },
  [ADMIN_ROUTES.dictionaries.project_categories]: {
    apiPath: 'project_categories',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'project_categories',
    description: 'Категории проектов.',
    editFields: ['name'],
    mode: 'list',
    recordKey: 'project_category',
    title: 'Категории проектов'
  },
  [ADMIN_ROUTES.dictionaries.cities]: {
    apiPath: 'cities',
    canExport: true,
    collectionKey: 'cities',
    description: 'Города и справочник локаций.',
    editFields: [
      'name',
      {
        control: 'select',
        name: 'country_id',
        options: { apiPath: 'countries/lookup', source: 'lookup' }
      }
    ],
    listColumns: ['id', 'name', 'country', 'updated_at'],
    mode: 'list',
    recordKey: 'city',
    title: 'Города'
  },
  [ADMIN_ROUTES.dictionaries.filmmakers]: {
    apiPath: 'filmmakers',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'filmmakers',
    description: 'Кинематографисты для фильмов и проектов.',
    editFields: ['full_name', 'type'],
    mode: 'list',
    recordKey: 'filmmaker',
    title: 'Кинематографисты'
  },
  [ADMIN_ROUTES.dictionaries.tags]: {
    apiPath: 'tags',
    canBatch: true,
    canCreate: true,
    canExport: true,
    collectionKey: 'tags',
    description: 'Системные теги.',
    editFields: ['name'],
    mode: 'list',
    recordKey: 'tag',
    title: 'Теги'
  },
  [ADMIN_ROUTES.education.institutions]: {
    apiPath: 'educational_institutions',
    canExport: true,
    collectionKey: 'educational_institutions',
    description: 'Учебные заведения и данные DaData.',
    editFields: ['popularity_rank'],
    listColumns: [
      'id',
      'name',
      'external_id',
      'popularity_rank',
      'faculties_count',
      'updated_at'
    ],
    mode: 'list',
    recordKey: 'educational_institution',
    title: 'Учебные заведения'
  },
  [ADMIN_ROUTES.system.mkrf_imports]: {
    apiPath: 'system/mkrf_imports',
    canExport: true,
    collectionKey: 'mkrf_imports',
    description:
      'История запусков импорта реестров МКРФ: прокатных удостоверений и удостоверений национальных фильмов.',
    listColumns: [
      'id',
      'dataset',
      'status',
      'version_number',
      'records_imported',
      'records_skipped',
      'finished_at'
    ],
    mode: 'list',
    readOnly: true,
    recordKey: 'mkrf_import',
    title: 'Импорты МКРФ'
  },
  [ADMIN_ROUTES.system.devices]: {
    apiPath: 'system/devices',
    canExport: true,
    collectionKey: 'devices',
    description: 'Устройства пользователей для push-уведомлений.',
    listColumns: [
      'id',
      'user',
      'device_model',
      'manufacturer',
      'platform',
      'created_at'
    ],
    mode: 'list',
    readOnly: true,
    recordKey: 'device',
    title: 'Устройства'
  },
  [ADMIN_ROUTES.system.notifications]: {
    apiPath: 'system/notifications',
    canExport: true,
    collectionKey: 'notifications',
    description: 'Системные уведомления пользователей.',
    listColumns: [
      'id',
      'user',
      'message',
      'viewed',
      'target_screen',
      'created_at'
    ],
    mode: 'list',
    readOnly: true,
    recordKey: 'notification',
    title: 'Уведомления'
  },
  [ADMIN_ROUTES.system.chat]: {
    apiPath: 'system/chat/users',
    collectionKey: 'chat_users',
    description: 'Пользователи чат-интеграции.',
    mode: 'list',
    readOnly: true,
    recordKey: 'chat_user',
    title: 'Чат-интеграция'
  },
  [ADMIN_ROUTES.system.bookmarks]: {
    apiPath: 'system/bookmarks',
    canExport: true,
    collectionKey: 'bookmarks',
    description: 'Закладки пользователей по кастингам и профилям.',
    listColumns: ['id', 'author', 'casting', 'created_at'],
    mode: 'list',
    readOnly: true,
    recordKey: 'bookmark',
    title: 'Закладки'
  },
  [ADMIN_ROUTES.settings]: {
    apiPath: 'profile',
    description: 'Профиль текущего администратора.',
    mode: 'detail',
    recordKey: 'admin_user',
    title: 'Настройки админки'
  }
};
